import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as EmailValidator from 'email-validator';
import { ActionType, VerifyParam } from 'rc-slider-captcha';

import { TrialAccountLogOutPopup } from '../../components/Popups';
import { PhoneCodePopup } from '../../components/Popups/PhoneCodePopup';
import {
  EmailAndPasswordTemplate,
  PhoneVerificationTemplate,
  RegisterTemplate,
} from '../../components/Templates';
import { RegionsCode } from '../../constants/region';
import { Theme } from '../../constants/Theme';
import {
  getWebPageLink,
  WebPageLink,
  WebPageType,
} from '../../constants/webPageType';
import { usePopup, useShareData } from '../../context';
import useRedirectHome from '../../hooks/useRedirectHome';
import useRegister from '../../hooks/useRegister';
import useRegisterQuery from '../../hooks/useRegisterQuery';
import { TranslationLocale } from '../../i18n';
import { RegisterState } from '../../models/SessionStore';
import {
  ResetPasswordResult,
  UpdateCurrentUserResult,
} from '../../models/UserStore';
import { FirstParameter } from '../../models/Utils';
import { useSessionStore } from '../../stores/SessionStore';
import { useSystemStore } from '../../stores/SystemStore';
import { useUserStore } from '../../stores/UserStore';
import verifyCaptcha from '../../utils/captcha';
import { getUiLocale } from '../../utils/i18n';
import { getRegionFromPhoneCode } from '../../utils/region';

interface State {
  registrationStep: RegistrationStep;
  name: string;
  phoneCode: string;
  phoneNumber: string;
  email: string;
  password: string;
  confirmPassword: string;
  isClicked: boolean;
  errorMessage: string;
  isLoading: boolean;
  captcha: number;
  showCaptcha: boolean;
  verified: boolean;
  verificationSentAt: number | undefined;
  verificationCode: string;
}

interface RegisterScreenProps {
  webPageType: WebPageType;
  isPhoneSignup: boolean;
}

enum RegistrationStep {
  SignUp,
  VerifyPhone,
  EmailAndPassword,
}

const { fetchCaptcha } = useSystemStore.getState();
const { sendPhoneVerification, validatePhone, updateCurrentUser } =
  useUserStore.getState();
const { deleteTrialAccount } = useSessionStore.getState();

const RegisterScreen: React.FC<RegisterScreenProps> = ({
  webPageType,
  isPhoneSignup,
}) => {
  const { registerAcc } = useRegister(webPageType);
  const navigate = useNavigate();
  const { webBoosterType, source, query } = useRegisterQuery();

  const userId = useSessionStore((state) => state.activeUserId);
  const isLoggedIn = !!userId;
  const isTrialUser = useUserStore((state) =>
    userId ? state.getProfile(userId)?.isTrialUser ?? false : false,
  );
  const storedState = useSessionStore((state) => state.registerStates);
  const [state, setState] = useState<State>({
    registrationStep: RegistrationStep.SignUp,
    name: '',
    phoneCode: '+852',
    phoneNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
    isClicked: false,
    errorMessage: '',
    isLoading: false,
    captcha: 0,
    showCaptcha: false,
    verified: false,
    verificationSentAt: undefined,
    verificationCode: '',
  });
  const [isDirty, setIsDirty] = useState(false);
  const { i18n } = useTranslation();
  const { sharedData } = useShareData();
  const captchaRef = useRef<number>(state.captcha);

  useRedirectHome(isLoggedIn && !isTrialUser, webPageType);

  const onNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setState((s) => ({ ...s, name: event.target.value, errorMessage: '' }));
  }, []);

  const onPhoneNumberChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({
        ...s,
        phoneNumber: event.target.value,
        errorMessage: '',
      }));
    },
    [],
  );

  const onEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setState((s) => ({ ...s, email: event.target.value, errorMessage: '' }));
  }, []);

  const onPasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({
        ...s,
        password: event.target.value,
        errorMessage: '',
      }));
    },
    [],
  );

  const onConfirmPasswordChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({
        ...s,
        confirmPassword: event.target.value,
        errorMessage: '',
      }));
    },
    [],
  );

  const onSetErrorMessage = useCallback((message: string) => {
    setState((s) => ({ ...s, errorMessage: message }));
  }, []);

  const onClickResendCode = useCallback(async () => {
    if (state.phoneNumber) {
      setState((s) => ({ ...s, isLoading: true }));
      const result = await sendPhoneVerification({
        region: getRegionFromPhoneCode(state.phoneCode) ?? RegionsCode.HK,
        phoneNumber: state.phoneNumber,
      });
      if (result.phoneVerification) {
        setState((s) => ({
          ...s,
          isLoading: false,
          verificationSentAt: result.phoneVerification?.verificationSentAt,
          registrationStep: RegistrationStep.VerifyPhone,
        }));
      } else if (
        result.result === UpdateCurrentUserResult.PhoneAlreadyRegistered
      ) {
        setState((s) => ({ ...s, isLoading: false, verified: false }));
        onSetErrorMessage(
          'This phone number is already registered with a JOYAJOY account.',
        );
      } else {
        setState((s) => ({ ...s, isLoading: false, verified: false }));
        onSetErrorMessage('Invalid phone number');
      }
    }
    return;
  }, [onSetErrorMessage, state.phoneCode, state.phoneNumber]);

  const updateTrialUser = useCallback(
    async (body: FirstParameter<typeof updateCurrentUser>) => {
      const response = await updateCurrentUser(body);
      const successPath = location.pathname.includes('wea-member')
        ? '/wea-member/register/success'
        : '/register/success';

      switch (response) {
        case UpdateCurrentUserResult.Success:
          navigate(successPath);
          break;
        case UpdateCurrentUserResult.EmailAlreadyRegistered:
          onSetErrorMessage(
            'This email address is already registered with a JOYAJOY account.',
          );
          break;
        default:
          onSetErrorMessage('Invalid email address format.');
          break;
      }
    },
    [navigate, onSetErrorMessage],
  );

  const onClickRegister = useCallback(async () => {
    if (state.verified) {
      setState((s) => ({
        ...s,
        isClicked: true,
        isLoading: true,
        errorMessage: '',
      }));

      if (state.password !== state.confirmPassword) {
        onSetErrorMessage('Confirm password does not match');
        setState((s) => ({ ...s, isLoading: false }));
        return;
      }

      if (isPhoneSignup) {
        await onClickResendCode();
        return;
      }

      if (isTrialUser) {
        await updateTrialUser({
          user: {
            email: isPhoneSignup ? undefined : state.email,
            password: state.password,
          },
        });
      } else {
        await registerAcc(
          {
            uiTheme: Theme.Light,
            interfaceLocale: getUiLocale(i18n.language as TranslationLocale),
            email: isPhoneSignup ? undefined : state.email,
            password: state.password,
            webBoosterType,
            source,
            referralCode: sharedData?.referralCode,
            // phoneNumber: isPhoneSignup ? state.phoneNumber : undefined,
          },
          onSetErrorMessage,
        );
      }
      // onSetErrorMessage(
      //   isPhoneSignup ? 'Invalid phone number' : 'Invalid email address',
      // );

      setState((s) => ({ ...s, isLoading: false, verified: false }));
    } else {
      onSetErrorMessage('Please verify the captcha');
    }
  }, [
    state.verified,
    state.password,
    state.confirmPassword,
    state.email,
    isPhoneSignup,
    isTrialUser,
    onSetErrorMessage,
    onClickResendCode,
    updateTrialUser,
    registerAcc,
    i18n.language,
    webBoosterType,
    source,
    sharedData?.referralCode,
  ]);

  useEffect(() => {
    captchaRef.current = state.captcha;
  }, [state.captcha]);

  const onRequest = useCallback(async () => {
    const res = await fetchCaptcha();
    if (res) {
      setState((s) => ({ ...s, captcha: res.solution }));

      const convertToDataURL = (data: number[]): Promise<string> => {
        return new Promise((resolve, reject) => {
          const arrayBuffer = new Uint8Array(data).buffer;
          const blob = new Blob([arrayBuffer], { type: 'image/png' });
          const reader = new FileReader();

          reader.onloadend = function () {
            resolve(reader.result as string);
          };

          reader.onerror = reject;

          reader.readAsDataURL(blob);
        });
      };

      const bgUrl = await convertToDataURL(res.data.background.data);
      const puzzleUrl = await convertToDataURL(res.data.slider.data);

      return { bgUrl, puzzleUrl };
    }
    return { bgUrl: '', puzzleUrl: '' };
  }, []);

  const onVerify = useCallback(async (data: VerifyParam) => {
    const res = await verifyCaptcha(captchaRef.current, {
      response: data.x,
      trail: data.trail,
    });
    if (res) {
      setState((s) => ({ ...s, verified: true, showCaptcha: false }));
    }
  }, []);

  const actionRef = useRef<ActionType>();

  const onClickCaptcha = useCallback(() => {
    setState((s) => ({ ...s, showCaptcha: true }));
  }, []);

  useEffect(() => {
    if (state.showCaptcha) {
      actionRef.current?.refresh(true);
    }
  }, [state.showCaptcha]);

  const onCloseCaptcha = useCallback(() => {
    setState((s) => ({ ...s, showCaptcha: false }));
  }, []);

  const onClickLogin = useCallback(() => {
    navigate(getWebPageLink(webPageType, WebPageLink.Login) + query);
  }, [navigate, query, webPageType]);

  const { showPopup, hidePopup } = usePopup();

  const onClickTrialLogout = useCallback(() => {
    const trialLogout = async () => {
      if (isTrialUser) {
        setState((s) => ({ ...s, isLoading: true }));
        hidePopup();
        await deleteTrialAccount();
        setState((s) => ({ ...s, isLoading: false }));
      }
    };

    showPopup(
      <TrialAccountLogOutPopup
        onPrimary={() => {
          trialLogout().catch(console.error);
        }}
        onCancel={hidePopup}
      />,
    );
  }, [hidePopup, isTrialUser, showPopup]);

  const onClickPhoneCode = useCallback(() => {
    showPopup(
      <PhoneCodePopup
        phoneCode={state.phoneCode}
        onPrimary={hidePopup}
        onCode={(selectedCode) => {
          setState((prevState) => ({ ...prevState, phoneCode: selectedCode }));
          hidePopup();
        }}
        webPageType={webPageType}
      />,
    );
  }, [showPopup, state.phoneCode, hidePopup, webPageType]);

  const onClickBack = useCallback(() => {
    setState((s) => ({
      ...s,
      registrationStep: RegistrationStep.SignUp,
      errorMessage: '',
      captcha: 0,
      showCaptcha: false,
      verified: false,
    }));
  }, []);

  const onClickVerifyCode = useCallback(
    async (code: string) => {
      setState((s) => ({
        ...s,
        isClicked: true,
        isLoading: true,
        errorMessage: '',
      }));

      const result = await validatePhone({
        region: getRegionFromPhoneCode(state.phoneCode) ?? RegionsCode.HK,
        phoneNumber: state.phoneNumber,
        verificationCode: code,
      });
      if (result === ResetPasswordResult.Success) {
        setState((s) => ({
          ...s,
          isLoading: false,
          verificationCode: code,
          isClicked: false,
          registrationStep: RegistrationStep.EmailAndPassword,
        }));
      } else {
        setState((s) => ({ ...s, isLoading: false }));
        onSetErrorMessage('Incorrect Code');
      }
    },
    [onSetErrorMessage, state.phoneCode, state.phoneNumber],
  );

  const onClickRegisterWithPhone = useCallback(async () => {
    setState((s) => ({
      ...s,
      isClicked: true,
      isLoading: true,
      errorMessage: '',
    }));

    if (state.password !== state.confirmPassword) {
      onSetErrorMessage('Confirm password does not match');
      setState((s) => ({ ...s, isLoading: false }));
      return;
    }

    const isEmail = EmailValidator.validate(state.email);

    if (isTrialUser) {
      await updateTrialUser({
        user: {
          username: isEmail ? undefined : state.email,
          email: isEmail ? state.email : undefined,
          phone: {
            region: getRegionFromPhoneCode(state.phoneCode) ?? RegionsCode.HK,
            phoneNumber: state.phoneNumber,
            verificationCode: state.verificationCode,
          },
          password: state.password,
          name: state.name,
        },
      });
    } else {
      await registerAcc(
        {
          uiTheme: Theme.Light,
          interfaceLocale: getUiLocale(i18n.language as TranslationLocale),
          username: isEmail ? undefined : state.email,
          email: isEmail ? state.email : undefined,
          phone: {
            region: getRegionFromPhoneCode(state.phoneCode) ?? RegionsCode.HK,
            phoneNumber: state.phoneNumber,
            verificationCode: state.verificationCode,
          },
          password: state.password,
          name: state.name,
          webBoosterType,
          source,
          referralCode: sharedData?.referralCode,
        },
        onSetErrorMessage,
      );
    }

    setState((s) => ({ ...s, isLoading: false, isClicked: false }));
  }, [
    i18n.language,
    isTrialUser,
    onSetErrorMessage,
    registerAcc,
    sharedData?.referralCode,
    source,
    state.confirmPassword,
    state.email,
    state.name,
    state.password,
    state.phoneCode,
    state.phoneNumber,
    state.verificationCode,
    updateTrialUser,
    webBoosterType,
  ]);

  useEffect(() => {
    // load stored state on mount
    if (storedState !== null) {
      const newState: State = {
        registrationStep: RegistrationStep.SignUp,
        name: storedState.name,
        phoneCode: storedState.phoneCode,
        phoneNumber: storedState.phoneNumber,
        email: storedState.email,
        password: storedState.password,
        confirmPassword: storedState.confirmPassword,
        isClicked: storedState.isClicked,
        errorMessage: '',
        isLoading: false,
        captcha: 0,
        showCaptcha: false,
        verified: false,
        verificationSentAt: storedState.verificationSentAt,
        verificationCode: storedState.verificationCode,
      };
      setState(newState);
      setIsDirty(false);
      useSessionStore.getState().setRegisterStates(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsDirty(true);
  }, [state]);

  useEffect(() => {
    // save state changes to session store
    const newState: RegisterState = {
      name: state.name,
      phoneCode: state.phoneCode,
      phoneNumber: state.phoneNumber,
      email: state.email,
      password: state.password,
      confirmPassword: state.confirmPassword,
      isClicked: state.isClicked,
      verificationSentAt: state.verificationSentAt,
      verificationCode: state.verificationCode,
    };
    if (isDirty) {
      useSessionStore.getState().setRegisterStates(newState);
    }
  }, [isDirty, state]);

  if (state.registrationStep === RegistrationStep.SignUp) {
    return (
      <RegisterTemplate
        webPageType={webPageType}
        isTrialUser={isTrialUser}
        name={{ value: state.name, onChange: onNameChange }}
        phoneCode={isPhoneSignup ? state.phoneCode : undefined}
        phoneNumber={
          isPhoneSignup
            ? { value: state.phoneNumber, onChange: onPhoneNumberChange }
            : undefined
        }
        email={
          isPhoneSignup
            ? undefined
            : { value: state.email, onChange: onEmailChange }
        }
        password={{ value: state.password, onChange: onPasswordChange }}
        confirmPassword={{
          value: state.confirmPassword,
          onChange: onConfirmPasswordChange,
        }}
        isClicked={state.isClicked}
        onClick={() => {
          onClickRegister().catch(console.error);
        }}
        errorMessage={state.errorMessage}
        isLoading={state.isLoading}
        showCaptcha={state.showCaptcha}
        onRequest={onRequest}
        onVerify={onVerify}
        actionRef={actionRef}
        onClickCaptcha={onClickCaptcha}
        onCloseCaptcha={onCloseCaptcha}
        verified={state.verified}
        onClickLogin={onClickLogin}
        onClickTrialLogout={onClickTrialLogout}
        isPhoneSignup={isPhoneSignup}
        onClickPhoneCode={onClickPhoneCode}
      />
    );
  }

  if (state.registrationStep === RegistrationStep.VerifyPhone) {
    return (
      <PhoneVerificationTemplate
        phoneNumber={`${state.phoneCode} ${state.phoneNumber}`}
        resendCountDown={(state.verificationSentAt ?? 0) + 60 * 1000}
        errorMessage={state.errorMessage}
        isLoading={state.isLoading}
        webPageType={webPageType}
        onClickConfirm={(code) => {
          onClickVerifyCode(code).catch(console.error);
        }}
        onClickResend={() => {
          onClickResendCode().catch(console.error);
        }}
        onClickBack={onClickBack}
      />
    );
  }

  if (state.registrationStep === RegistrationStep.EmailAndPassword) {
    return (
      <EmailAndPasswordTemplate
        email={{ value: state.email, onChange: onEmailChange }}
        password={{ value: state.password, onChange: onPasswordChange }}
        confirmPassword={{
          value: state.confirmPassword,
          onChange: onConfirmPasswordChange,
        }}
        isClicked={state.isClicked}
        errorMessage={state.errorMessage}
        isLoading={state.isLoading}
        webPageType={webPageType}
        onClick={() => {
          onClickRegisterWithPhone().catch(console.error);
        }}
        onClickBack={onClickBack}
      />
    );
  }

  return <></>;
};

export default RegisterScreen;
